import React from 'react';
import { 
  Package, 
  Layers, 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Clock,
  Users,
  Target
} from 'lucide-react';

const ManufacturingDashboard = () => {
  const stats = [
    {
      title: 'Active Batches',
      value: '8',
      change: '+2',
      changeType: 'positive',
      icon: Layers,
      color: 'blue'
    },
    {
      title: 'Naloxone Kits Completed Today',
      value: '1,250',
      change: '+18%',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'green'
    },
    {
      title: 'Production Target',
      value: '92%',
      change: '+7%',
      changeType: 'positive',
      icon: Target,
      color: 'red'
    },
    {
      title: 'Low Stock Components',
      value: '5',
      change: '+1',
      changeType: 'negative',
      icon: AlertTriangle,
      color: 'yellow'
    }
  ];

  const activeBatches = [
    { batch: 'NAL-2024-001', kitType: 'Take-Home Naloxone Kit', progress: 85, worker: 'Maria Rodriguez', priority: 'High' },
    { batch: 'NAL-2024-002', kitType: 'Training Naloxone Kit', progress: 60, worker: 'James Chen', priority: 'Medium' },
    { batch: 'NAL-2024-003', kitType: 'Professional Naloxone Kit', progress: 95, worker: 'Sarah Johnson', priority: 'High' },
    { batch: 'NAL-2024-004', kitType: 'Community Naloxone Kit', progress: 30, worker: 'Mike Davis', priority: 'Low' },
    { batch: 'NAL-2024-005', kitType: 'Emergency Naloxone Kit', progress: 45, worker: 'Lisa Wang', priority: 'Medium' }
  ];

  const lowStockComponents = [
    { sku: 'NAL-001', name: 'Naloxone Nasal Spray 4mg', current: 120, minimum: 500, location: 'A1-B2' },
    { sku: 'INST-002', name: 'Instruction Cards (EN/FR)', current: 85, minimum: 300, location: 'B3-C1' },
    { sku: 'FACE-003', name: 'Face Shield/Barrier', current: 200, minimum: 600, location: 'C2-D1' },
    { sku: 'GLOV-004', name: 'Nitrile Gloves (Pairs)', current: 150, minimum: 400, location: 'B1-C4' }
  ];

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'text-red-600 bg-red-50',
      'Medium': 'text-yellow-600 bg-yellow-50',
      'Low': 'text-green-600 bg-green-50'
    };
    return colors[priority] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Manufacturing Dashboard</h1>
        <p className="text-gray-600 text-sm lg:text-base">Real-time overview of your Naloxone kit production</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-center justify-between mb-3 lg:mb-4">
                <div className={`p-2 lg:p-3 rounded-lg bg-${stat.color}-100`}>
                  <Icon className={`h-5 w-5 lg:h-6 lg:w-6 text-${stat.color}-600`} />
                </div>
                <div className={`flex items-center text-xs lg:text-sm font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.changeType === 'positive' ? 
                    <TrendingUp className="h-3 w-3 lg:h-4 lg:w-4 mr-1" /> : 
                    <TrendingDown className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
                  }
                  {stat.change}
                </div>
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-xs lg:text-sm text-gray-600">{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
        {/* Active Batches */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 lg:p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Active Production Batches</h3>
          </div>
          <div className="p-4 lg:p-6">
            <div className="space-y-4">
              {activeBatches.map((batch) => (
                <div key={batch.batch} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                    <div>
                      <p className="font-medium text-gray-900 text-sm lg:text-base">{batch.batch}</p>
                      <p className="text-xs lg:text-sm text-gray-600">{batch.kitType}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(batch.priority)} self-start sm:self-center`}>
                      {batch.priority}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex justify-between text-xs lg:text-sm mb-1">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium text-gray-900">{batch.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${batch.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-xs lg:text-sm text-gray-600">
                    <Users className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
                    <span>Worker: {batch.worker}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Low Stock Components */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 lg:p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              Component Stock Alerts
            </h3>
          </div>
          <div className="p-4 lg:p-6">
            <div className="space-y-4">
              {lowStockComponents.map((component) => (
                <div key={component.sku} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-1">
                    <p className="font-medium text-gray-900 text-sm lg:text-base">{component.name}</p>
                    <span className="text-xs lg:text-sm font-medium text-red-600">SKU: {component.sku}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs lg:text-sm text-gray-600 mb-2 gap-1">
                    <span>Current: {component.current} | Min: {component.minimum}</span>
                    <span>Location: {component.location}</span>
                  </div>
                  <div className="bg-red-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${Math.min((component.current / component.minimum) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Production Summary */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Production Summary</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <div className="text-center">
            <p className="text-xl lg:text-2xl font-bold text-red-600">1,250</p>
            <p className="text-xs lg:text-sm text-gray-600">Naloxone Kits Completed</p>
          </div>
          <div className="text-center">
            <p className="text-xl lg:text-2xl font-bold text-green-600">8</p>
            <p className="text-xs lg:text-sm text-gray-600">Batches Active</p>
          </div>
          <div className="text-center">
            <p className="text-xl lg:text-2xl font-bold text-blue-600">6</p>
            <p className="text-xs lg:text-sm text-gray-600">Workers On Shift</p>
          </div>
          <div className="text-center">
            <p className="text-xl lg:text-2xl font-bold text-yellow-600">98.2%</p>
            <p className="text-xs lg:text-sm text-gray-600">Quality Pass Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManufacturingDashboard;